import React, { useEffect, useRef, useState } from "react";

/**
 * Plain-input twin of DebouncedTextField's recent-values dropdown, for forms that
 * are NOT Formik-backed (calibre add/edit forms use native <input> + useState).
 *
 * Renders a native <input> that behaves exactly like the one it replaces — it takes
 * the same `value`/`onChange`/`style`/`type` etc. — plus a click/keyboard-fill
 * dropdown of the last 10 distinct values entered under `rememberKey` (localStorage,
 * per-browser, shared across every input using the same key). Purely additive:
 * invisible until values exist, and picking one calls the SAME `onChange` (with a
 * synthetic {target:{value}}) so any linked-field logic (HZ↔VPH, Hours↔Days) still runs.
 *
 * Keyboard: ↓/↑ highlight, Enter/Tab accept the highlighted item, Esc closes. With
 * nothing highlighted, keys behave normally.
 */

const RECENT_MAX = 10;
const recentKey = (k: string) => `recentValues:${k}`;

function readRecent(k: string): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(recentKey(k)) || "null");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function saveRecent(k: string, value: any) {
  const v = (value ?? "").toString().trim();
  if (!v) return;
  const list = [v, ...readRecent(k).filter((x) => x !== v)].slice(0, RECENT_MAX);
  try {
    localStorage.setItem(recentKey(k), JSON.stringify(list));
  } catch {
    /* localStorage full/unavailable — silently skip */
  }
}

interface Props {
  rememberKey: string;
  value: any;
  onChange: (e: any) => void;
  onBlur?: (e: any) => void;
  onFocus?: (e: any) => void;
  onKeyDown?: (e: any) => void;
  style?: React.CSSProperties;
  wrapperStyle?: React.CSSProperties;
  type?: string;
  [key: string]: any;
}

export function RecentValuesInput({
  rememberKey,
  value,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  style,
  wrapperStyle,
  type = "text",
  ...rest
}: Props) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [highlight, setHighlight] = useState(-1);
  const blurTimerRef = useRef<any>(null);
  // Mirrors the value synchronously so a blur commit saves the picked/typed value.
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    return () => {
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    };
  }, []);

  const matches = (text: any) => {
    const q = (text ?? "").toString().trim().toLowerCase();
    const all = readRecent(rememberKey);
    return q ? all.filter((s) => s.toLowerCase().includes(q) && s.toLowerCase() !== q) : all;
  };

  const refresh = (text: any) => {
    setSuggestions(matches(text));
    setHighlight(-1);
  };

  const emit = (v: string) => onChange({ target: { value: v } });

  const handleChange = (e: any) => {
    onChange(e);
    refresh(e.target.value);
    if (!open) setOpen(true);
  };

  const handleFocus = (e: any) => {
    refresh(value);
    setOpen(true);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e: any) => {
    blurTimerRef.current = setTimeout(() => setOpen(false), 150);
    saveRecent(rememberKey, valueRef.current);
    if (onBlur) onBlur(e);
  };

  const pick = (s: string) => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    valueRef.current = s;
    emit(s);
    saveRecent(rememberKey, s);
    setOpen(false);
    setHighlight(-1);
  };

  const handleKeyDown = (e: any) => {
    if (onKeyDown) onKeyDown(e);

    // Open the list on ArrowDown even when closed.
    if (e.key === "ArrowDown" && (!open || suggestions.length === 0)) {
      const next = matches(value);
      if (next.length) {
        e.preventDefault();
        setSuggestions(next);
        setOpen(true);
        setHighlight(0);
      }
      return;
    }
    if (!open || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (highlight >= 0 && highlight < suggestions.length) {
        e.preventDefault();
        pick(suggestions[highlight]);
      }
    } else if (e.key === "Tab") {
      if (highlight >= 0 && highlight < suggestions.length) pick(suggestions[highlight]);
      else setOpen(false);
    } else if (e.key === "Escape") {
      setOpen(false);
      setHighlight(-1);
    }
  };

  return (
    <div style={{ position: "relative", width: "100%", ...wrapperStyle }}>
      <input
        {...rest}
        type={type}
        style={style}
        value={value ?? ""}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      {open && suggestions.length > 0 && (
        <ul
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 40,
            margin: 0,
            padding: 0,
            listStyle: "none",
            background: "#fff",
            border: "1px solid #ccc",
            borderRadius: 3,
            minWidth: "100%",
            width: "max-content",
            maxWidth: 280,
            maxHeight: 180,
            overflowY: "auto",
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          }}
        >
          {suggestions.map((s, i) => (
            <li
              key={i}
              // onMouseDown + preventDefault so the pick fires before the input blur.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s);
              }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                padding: "4px 8px",
                fontSize: 12,
                cursor: "pointer",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                background: i === highlight ? "#e3f0ff" : "#fff",
                borderBottom: i < suggestions.length - 1 ? "1px solid #eee" : "none",
              }}
              title={s}
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}